/**
 * Kebab-name → TSX icon component map.
 *
 * Prefer the **short** names below. Longer legacy keys remain as aliases.
 * Use with `<AppIcon name="consult" />` or `<Clickable icon="consult" />`.
 */
import type { ComponentType } from 'react';
import type { AppIconProps } from './iconBase';
import { AlertIcon } from './AlertIcon';
import { ArrowBackIcon } from './ArrowBackIcon';
import { ArrowDownIcon } from './ArrowDownIcon';
import { ArrowForwardIcon } from './ArrowForwardIcon';
import { ArrowLeftIcon } from './ArrowLeftIcon';
import { ArrowRightIcon } from './ArrowRightIcon';
import { ArrowUpIcon } from './ArrowUpIcon';
import { BalanceIcon } from './BalanceIcon';
import { BellIcon } from './BellIcon';
import { CalendarIcon } from './CalendarIcon';
import { CallIcon } from './CallIcon';
import { CardIcon } from './CardIcon';
import { CartIcon } from './CartIcon';
import { ChatIcon } from './ChatIcon';
import { CheckIcon } from './CheckIcon';
import { ChecksIcon } from './ChecksIcon';
import { ChevronDownIcon } from './ChevronDownIcon';
import { ChevronForwardIcon } from './ChevronForwardIcon';
import { ConsultExpertDecoIcon } from './ConsultExpertDecoIcon';
import { DashboardIcon } from './DashboardIcon';
import { DashboardOutlineIcon } from './DashboardOutlineIcon';
import { DocIcon } from './DocIcon';
import { DriverIcon } from './DriverIcon';
import { EditIcon } from './EditIcon';
import { EllipseOutlineIcon } from './EllipseOutlineIcon';
import { ExpandIcon } from './ExpandIcon';
import { EyeIcon } from './EyeIcon';
import { FingerprintIcon } from './FingerprintIcon';
import { FlameIcon } from './FlameIcon';
import { Freeze2Icon } from './Freeze2Icon';
import { FreezeIcon } from './FreezeIcon';
import { GainIcon } from './GainIcon';
import { GiftIcon } from './GiftIcon';
import { HealthAlertIcon } from './HealthAlertIcon';
import { HealthQuizDecoIcon } from './HealthQuizDecoIcon';
import { HeartFillIcon } from './HeartFillIcon';
import { HeartIcon } from './HeartIcon';
import { InstagramIcon } from './InstagramIcon';
import { BalanceGearsIcon } from './BalanceGearsIcon';
import { JourneyGoalGainIcon } from './JourneyGoalGainIcon';
import { JourneyGoalLossIcon } from './JourneyGoalLossIcon';
import { JourneyGoalMaintainIcon } from './JourneyGoalMaintainIcon';
import { JourneyIcon } from './JourneyIcon';
import { TrendDownChartIcon } from './TrendDownChartIcon';
import { TrendUpChartIcon } from './TrendUpChartIcon';
import { LanguageIcon } from './LanguageIcon';
import { ListIcon } from './ListIcon';
import { LockIcon } from './LockIcon';
import { LogoutIcon } from './LogoutIcon';
import { LossIcon } from './LossIcon';
import { MaintainIcon } from './MaintainIcon';
import { MapIcon } from './MapIcon';
import { MealIcon } from './MealIcon';
import { MedkitIcon } from './MedkitIcon';
import { MinusIcon } from './MinusIcon';
import { NewsIcon } from './NewsIcon';
import { NewspaperIcon } from './NewspaperIcon';
import { NoeyeIcon } from './NoeyeIcon';
import { NomealIcon } from './NomealIcon';
import { PackageIcon } from './PackageIcon';
import { PlusIcon } from './PlusIcon';
import { ReceiptIcon } from './ReceiptIcon';
import { RefreshIcon } from './RefreshIcon';
import { SearchIcon } from './SearchIcon';
import { SettingsIcon } from './SettingsIcon';
import { SnowIcon } from './SnowIcon';
import { StarIcon } from './StarIcon';
import { SunnyIcon } from './SunnyIcon';
import { TagIcon } from './TagIcon';
import { TimeIcon } from './TimeIcon';
import { TrashIcon } from './TrashIcon';
import { TrophyIcon } from './TrophyIcon';
import { UserIcon } from './UserIcon';
import { UsersIcon } from './UsersIcon';
import { WalkIcon } from './WalkIcon';
import { WalletIcon } from './WalletIcon';
import { WaterIcon } from './WaterIcon';
import { WbalanceIcon } from './WbalanceIcon';
import { WhatsappIcon } from './WhatsappIcon';
import { XIcon } from './XIcon';
import { YoutubeIcon } from './YoutubeIcon';

/**
 * Preferred (short) icon names — use these in new code.
 * Full map below also accepts legacy long aliases.
 */
export const APP_ICON_NAMES = [
  'alert',
  'balance',
  'bell',
  'calendar',
  'call',
  'card',
  'cart',
  'chat',
  'check',
  'checks',
  'consult',
  'dash',
  'dashboard',
  'doc',
  'driver',
  'edit',
  'expand',
  'eye',
  'eye-off',
  'fingerprint',
  'flame',
  'freeze',
  'gain',
  'gift',
  'goal-gain',
  'goal-loss',
  'goal-maintain',
  'balance-gears',
  'trend-up-chart',
  'trend-down-chart',
  'heart',
  'hearted',
  'instagram',
  'journey',
  'language',
  'left',
  'list',
  'lock',
  'logout',
  'loss',
  'maintain',
  'map',
  'meal',
  'meal-off',
  'medkit',
  'minus',
  'news',
  'newspaper',
  'package',
  'plus',
  'quiz',
  'receipt',
  'refresh',
  'right',
  'scale',
  'search',
  'settings',
  'snow',
  'star',
  'sunny',
  'tag',
  'time',
  'trash',
  'trophy',
  'user',
  'users',
  'walk',
  'wallet',
  'water',
  'whatsapp',
  'x',
  'youtube',
] as const;

export type PreferredAppIconName = (typeof APP_ICON_NAMES)[number];

export const appIconMap = {
  // —— preferred short names ——
  alert: AlertIcon,
  balance: BalanceIcon,
  bell: BellIcon,
  calendar: CalendarIcon,
  call: CallIcon,
  card: CardIcon,
  cart: CartIcon,
  chat: ChatIcon,
  check: CheckIcon,
  checks: ChecksIcon,
  consult: ConsultExpertDecoIcon,
  dash: DashboardOutlineIcon,
  dashboard: DashboardIcon,
  doc: DocIcon,
  driver: DriverIcon,
  edit: EditIcon,
  expand: ExpandIcon,
  eye: EyeIcon,
  'eye-off': NoeyeIcon,
  fingerprint: FingerprintIcon,
  flame: FlameIcon,
  freeze: FreezeIcon,
  gain: GainIcon,
  gift: GiftIcon,
  'goal-gain': JourneyGoalGainIcon,
  'goal-loss': JourneyGoalLossIcon,
  'goal-maintain': JourneyGoalMaintainIcon,
  'balance-gears': BalanceGearsIcon,
  'trend-up-chart': TrendUpChartIcon,
  'trend-down-chart': TrendDownChartIcon,
  heart: HeartIcon,
  hearted: HeartFillIcon,
  instagram: InstagramIcon,
  journey: JourneyIcon,
  language: LanguageIcon,
  left: ArrowLeftIcon,
  list: ListIcon,
  lock: LockIcon,
  logout: LogoutIcon,
  loss: LossIcon,
  maintain: MaintainIcon,
  map: MapIcon,
  meal: MealIcon,
  'meal-off': NomealIcon,
  medkit: MedkitIcon,
  minus: MinusIcon,
  news: NewsIcon,
  newspaper: NewspaperIcon,
  package: PackageIcon,
  plus: PlusIcon,
  quiz: HealthQuizDecoIcon,
  receipt: ReceiptIcon,
  refresh: RefreshIcon,
  right: ArrowRightIcon,
  scale: WbalanceIcon,
  search: SearchIcon,
  settings: SettingsIcon,
  snow: SnowIcon,
  star: StarIcon,
  sunny: SunnyIcon,
  tag: TagIcon,
  time: TimeIcon,
  trash: TrashIcon,
  trophy: TrophyIcon,
  user: UserIcon,
  users: UsersIcon,
  walk: WalkIcon,
  wallet: WalletIcon,
  water: WaterIcon,
  whatsapp: WhatsappIcon,
  x: XIcon,
  youtube: YoutubeIcon,

  // —— legacy aliases (keep working) ——
  'arrow-back': ArrowBackIcon,
  'arrow-down': ArrowDownIcon,
  'arrow-forward': ArrowForwardIcon,
  'arrow-left': ArrowLeftIcon,
  'arrow-right': ArrowRightIcon,
  'arrow-up': ArrowUpIcon,
  'chevron-back': ArrowBackIcon,
  'chevron-down': ChevronDownIcon,
  'chevron-forward': ChevronForwardIcon,
  'chevron-left': ArrowLeftIcon,
  'chevron-right': ChevronForwardIcon,
  'consult-expert-deco': ConsultExpertDecoIcon,
  'dashboard-outline': DashboardOutlineIcon,
  'ellipse-outline': EllipseOutlineIcon,
  'freeze2': Freeze2Icon,
  'health-alert': HealthAlertIcon,
  'health-quiz-deco': HealthQuizDecoIcon,
  'heart-fill': HeartFillIcon,
  'journey-goal-gain': JourneyGoalGainIcon,
  'journey-goal-loss': JourneyGoalLossIcon,
  'journey-goal-maintain': JourneyGoalMaintainIcon,
  /** @deprecated use `meal` */
  menu: MealIcon,
  /** @deprecated use `eye-off` */
  noeye: NoeyeIcon,
  /** @deprecated use `meal-off` */
  nomeal: NomealIcon,
  notifications: BellIcon,
  reload: RefreshIcon,
  'star-full': StarIcon,
  'star-outline': StarIcon,
  /** Secondary freeze glyph (legacy name) */
  unfreeze: Freeze2Icon,
  /** @deprecated use `scale` */
  wbalance: WbalanceIcon,
} as const satisfies Record<string, ComponentType<AppIconProps>>;

export type AppIconName = keyof typeof appIconMap;
